import { useParams, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faEye, faPencil } from '@fortawesome/free-solid-svg-icons';
import { useHasPermission } from '@/hooks/use-permissions';
import { SkeletonTable } from '@/components/skeletons';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/cn';
import { useProcedureVersions } from './hooks/useProcedureTemplates';
import { ProcedureStatusBadge } from './components/ProcedureStatusBadge';
import { ROUTES } from '@/routes/routes';

export function ProcedureVersionHistoryPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const can = useHasPermission();

  const { data: versions = [], isLoading, error: loadError } = useProcedureVersions(id);

  if (isLoading) return <SkeletonTable />;
  if (loadError) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-center">
        <p className="text-sm text-destructive">Failed to load version history</p>
        <button
          onClick={() => navigate(ROUTES.PROCEDURES)}
          className="mt-3 text-sm text-primary hover:underline"
        >
          Back to Library
        </button>
      </div>
    );
  }

  const latestVersion = versions[0];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={() =>
            latestVersion
              ? navigate(
                  latestVersion.status === 'DRAFT'
                    ? ROUTES.PROCEDURE_EDIT.replace(':id', latestVersion.id)
                    : ROUTES.PROCEDURE_VIEW.replace(':id', latestVersion.id),
                )
              : navigate(ROUTES.PROCEDURES)
          }
          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-border text-muted-foreground hover:bg-muted transition-colors"
        >
          <FontAwesomeIcon icon={faArrowLeft} className="h-4 w-4" />
        </button>
        <div>
          <h1 className="text-xl font-semibold text-foreground">Version History</h1>
          {latestVersion && (
            <p className="mt-0.5 text-sm text-muted-foreground">
              {versions.length} version{versions.length !== 1 ? 's' : ''}
            </p>
          )}
        </div>
      </div>

      {/* Empty state */}
      {versions.length === 0 && (
        <div className="rounded-xl border border-border py-20 text-center">
          <p className="text-sm text-muted-foreground">No version history available.</p>
          <button
            onClick={() => navigate(ROUTES.PROCEDURES)}
            className="mt-3 text-sm text-primary hover:underline"
          >
            Back to Library
          </button>
        </div>
      )}

      {/* Timeline */}
      {versions.length > 0 && (
        <div className="relative">
          {/* Vertical timeline line */}
          <div className="absolute left-[19px] top-4 bottom-4 w-px bg-border" aria-hidden />

          <div className="space-y-3">
            {versions.map((version, idx) => (
              <div key={version.id} className="relative flex items-start gap-4">
                {/* Timeline dot */}
                <div
                  className={cn(
                    'relative z-10 flex h-10 w-10 shrink-0 items-center justify-center rounded-full border-2 text-sm font-bold',
                    version.status === 'PUBLISHED'
                      ? 'border-primary bg-primary/10 text-primary'
                      : version.status === 'DRAFT'
                        ? 'border-amber-400 bg-amber-500/10 text-amber-700'
                        : 'border-border bg-muted text-muted-foreground',
                  )}
                >
                  v{version.versionNumber}
                </div>

                {/* Version card */}
                <div
                  className={cn(
                    'flex-1 rounded-xl border bg-card p-4 transition-colors',
                    idx === 0 ? 'border-primary/30' : 'border-border',
                  )}
                >
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="text-sm font-semibold text-foreground">
                          Version {version.versionNumber}
                        </span>
                        {idx === 0 && (
                          <span className="rounded-full bg-primary/10 px-2 py-0.5 text-[10px] font-medium text-primary">
                            Latest
                          </span>
                        )}
                        <ProcedureStatusBadge status={version.status} />
                      </div>
                      <div className="mt-1.5 flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
                        <span>By {version.createdBy.fullName}</span>
                        <span>
                          Created{' '}
                          {new Date(version.createdAt).toLocaleDateString(undefined, {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric',
                          })}
                        </span>
                        {version.publishedAt && (
                          <span>
                            Published{' '}
                            {new Date(version.publishedAt).toLocaleDateString(undefined, {
                              year: 'numeric',
                              month: 'short',
                              day: 'numeric',
                            })}
                          </span>
                        )}
                        {version.archivedAt && (
                          <span>
                            Archived{' '}
                            {new Date(version.archivedAt).toLocaleDateString(undefined, {
                              year: 'numeric',
                              month: 'short',
                              day: 'numeric',
                            })}
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="flex shrink-0 items-center gap-2">
                      {version.status === 'DRAFT' && can('procedure:update') && (
                        <Button
                          variant="ghost"
                          onClick={() => navigate(ROUTES.PROCEDURE_EDIT.replace(':id', version.id))}
                          className="h-8 text-xs"
                        >
                          <FontAwesomeIcon icon={faPencil} className="mr-1.5 h-3 w-3" />
                          Edit
                        </Button>
                      )}
                      {version.status !== 'DRAFT' && (
                        <Button
                          variant="ghost"
                          onClick={() => navigate(ROUTES.PROCEDURE_VIEW.replace(':id', version.id))}
                          className="h-8 text-xs"
                        >
                          <FontAwesomeIcon icon={faEye} className="mr-1.5 h-3 w-3" />
                          View
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Back to library */}
      <div className="pt-2">
        <button
          type="button"
          onClick={() => navigate(ROUTES.PROCEDURES)}
          className="text-sm text-muted-foreground hover:text-foreground transition-colors"
        >
          ← Back to Procedure Library
        </button>
      </div>
    </div>
  );
}
